import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Truck, Plus, RefreshCw, MapPin, Package, Clock, CheckCircle2, XCircle,
  Signature, ArrowRight, User as UserIcon, Phone as PhoneIcon, FileText, Printer, Trash2, Pencil,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/utils-format";
import { ForceDeleteDialog } from "@/components/ForceDeleteDialog";
import { DispatchItemsEditor } from "@/components/DispatchItemsEditor";

const STATUS_META = {
  dispatched: { label: "Dispatched", tone: "bg-blue-100 text-blue-800 border-blue-200", icon: Truck },
  in_transit: { label: "In Transit", tone: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  delivered: { label: "Delivered", tone: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  signed: { label: "Signed", tone: "bg-emerald-600 text-white border-emerald-700", icon: Signature },
  cancelled: { label: "Cancelled", tone: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
};

const ALL_STATUSES = ["dispatched", "in_transit", "delivered", "signed", "cancelled"];

export default function WarehouseDispatchPage() {
  const { user } = useAuth();
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    const pid = searchParams.get("product_id");
    const itemsRaw = searchParams.get("items");
    if (!pid && !itemsRaw) return;
    const toBranch = searchParams.get("to_branch_id") || "";
    (async () => {
      let wanted = [];
      try { wanted = itemsRaw ? JSON.parse(itemsRaw) : [{ product_id: pid, quantity: "" }]; } catch { wanted = []; }
      const products = [];
      for (const w of wanted) {
        try {
          const { data } = await api.get(`/products/${w.product_id}`);
          products.push({ ...data, prefill_qty: w.quantity === 0 || w.quantity ? String(w.quantity) : "" });
        } catch { /* skip missing */ }
      }
      if (products.length < wanted.length) toast.error(`${wanted.length - products.length} product(s) could not be loaded`);
      setPrefill({ to_branch_id: toBranch, products });
      setCreateOpen(true);
      setSearchParams({}, { replace: true });
    })();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const { data } = await api.get(`/dispatch${q}`);
      setDispatches(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dispatches");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const kpis = useMemo(() => {
    const s = { dispatched: 0, in_transit: 0, delivered: 0, signed: 0, cancelled: 0 };
    dispatches.forEach((d) => { s[d.current_status] = (s[d.current_status] || 0) + 1; });
    return s;
  }, [dispatches]);

  return (
    <div className="space-y-6" data-testid="warehouse-dispatch-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-[Outfit] text-2xl font-semibold text-[#2C1810] flex items-center gap-2">
            <Truck className="w-6 h-6 text-[#C05621]" />
            HO Warehouse Dispatch
          </h1>
          <p className="text-sm text-[#8B7355] mt-1">
            Track every stock transfer leaving the warehouse — driver, vehicle, e-way and status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="dispatch-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="dispatch-create-btn"
            className="bg-[#C05621] hover:bg-[#9C4221] text-white"
          >
            <Plus className="w-4 h-4 mr-1" /> New Dispatch
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {ALL_STATUSES.map((s) => {
          const meta = STATUS_META[s];
          const Icon = meta.icon;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
              data-testid={`dispatch-kpi-${s}`}
              className={`text-left bg-white border rounded-lg p-3 hover:border-[#C05621] transition ${
                statusFilter === s ? "border-[#C05621] ring-2 ring-[#C05621]/20" : "border-[#EAE5D9]"
              }`}
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#8B7355]">
                <Icon className="w-3.5 h-3.5" /> {meta.label}
              </div>
              <div className="text-2xl font-semibold text-[#2C1810] mt-1">{kpis[s] || 0}</div>
            </button>
          );
        })}
      </div>

      {/* Filter chip */}
      {statusFilter !== "all" && (
        <div className="text-xs text-[#8B7355]">
          Filtering by <span className="font-medium text-[#2C1810]">{STATUS_META[statusFilter]?.label}</span>
          <button onClick={() => setStatusFilter("all")} className="ml-2 text-[#C05621] hover:underline" data-testid="dispatch-clear-filter">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Number</th>
              <th className="px-4 py-3">Route</th>
              <th className="px-4 py-3">Driver / Vehicle</th>
              <th className="px-4 py-3">E-Way</th>
              <th className="px-4 py-3">Items</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#8B7355]">Loading dispatches…</td></tr>
            )}
            {!loading && dispatches.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#8B7355]">
                No dispatches yet. Click <span className="font-medium">"New Dispatch"</span> to log the first one.
              </td></tr>
            )}
            {!loading && dispatches.map((d) => {
              const meta = STATUS_META[d.current_status] || STATUS_META.dispatched;
              return (
                <tr key={d.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`dispatch-row-${d.id}`}>
                  <td className="px-4 py-3 font-mono text-[12px] font-medium">{d.number}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-[#2C1810]">
                      <span className="font-medium">{d.from_branch_code || "?"}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-[#8B7355]" />
                      <span className="font-medium">{d.to_branch_code || "?"}</span>
                    </div>
                    <div className="text-[11px] text-[#8B7355] mt-0.5">
                      {d.from_branch_name} → {d.to_branch_name}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[#2C1810]">{d.driver_name || "—"}</div>
                    <div className="text-[11px] text-[#8B7355]">
                      {d.vehicle_no || "No vehicle"}{d.driver_phone ? ` · ${d.driver_phone}` : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[12px] font-mono text-[#5C4033]">{d.eway_no || "—"}</td>
                  <td className="px-4 py-3 text-[12px] text-[#5C4033]">
                    {d.items_summary || (d.transfer_id ? "Linked" : "—")}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`border ${meta.tone}`}>{meta.label}</Badge>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[#8B7355]">{fmtDate(d.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printDispatchNote(d)}
                        data-testid={`dispatch-print-${d.id}`}
                        title="Print dispatch note with Transfer ID barcode"
                      >
                        <Printer className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDetailId(d.id)}
                        data-testid={`dispatch-view-${d.id}`}
                      >
                        View
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <CreateDispatchDialog
          prefill={prefill}
          onClose={() => { setCreateOpen(false); setPrefill(null); }}
          onCreated={() => { setCreateOpen(false); setPrefill(null); load(); }}
        />
      )}
      {detailId && (
        <DispatchDetailDialog
          id={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => load()}
          user={user}
        />
      )}
    </div>
  );
}

// ---------- Dispatch Note (printable, with Transfer ID barcode) ----------
function escapeHtml(str) {
  return String(str ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function printDispatchNote(d) {
  if (!d) return;
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return;
  const items = d.items && d.items.length ? d.items : (d.transfer?.items || []);
  const fmtRs = (n) => (n == null || Number(n) <= 0) ? "—" : "₹" + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totalValue = items.reduce((s, it) => s + (Number(it.value) || (Number(it.unit_cost) || 0) * (Number(it.quantity) || 0)), 0);
  const itemRows = items.map((it) => `
    <tr>
      <td>${escapeHtml(it.name || it.product_id)}</td>
      <td>${escapeHtml(it.sku || "—")}</td>
      <td style="text-align:right">${escapeHtml(it.quantity)}</td>
      <td>${escapeHtml(it.unit || "")}</td>
      <td style="text-align:right">${fmtRs(it.unit_cost)}</td>
      <td style="text-align:right">${fmtRs(it.value ?? (Number(it.unit_cost) || 0) * (Number(it.quantity) || 0))}</td>
    </tr>`).join("");
  w.document.write(`<html><head><title>Dispatch Note — ${escapeHtml(d.number)}</title>
    <style>
      @media print { @page { size: A5; margin: 8mm; } }
      body { font-family: 'Inter', 'Arial', sans-serif; margin: 0; padding: 12mm; color: #2C1810; }
      .brand { font-size: 20px; font-weight: 800; letter-spacing: 0.5px; color: #C05621; }
      .subtitle { font-size: 11px; color: #8B7355; margin-top: 2px; }
      .barcode-box { text-align: center; margin: 14px 0; padding: 10px; border: 2px dashed #C05621; border-radius: 6px; }
      .barcode-box .caption { font-size: 10px; color: #8B7355; margin-top: 4px; }
      .barcode-box .transfer-id { font-size: 16px; font-weight: 700; letter-spacing: 1px; margin-top: 2px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 14px 0; font-size: 12px; }
      .field .k { font-size: 10px; text-transform: uppercase; color: #8B7355; }
      .field .v { font-weight: 600; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
      th { text-align: left; font-size: 10px; text-transform: uppercase; color: #8B7355; border-bottom: 1px solid #EAE5D9; padding: 4px 6px; }
      td { padding: 5px 6px; border-bottom: 1px solid #F3EEE4; }
      .footer-note { margin-top: 16px; font-size: 11px; color: #5C4033; background: #FDFBF7; border: 1px solid #EAE5D9; border-radius: 6px; padding: 8px 10px; }
      hr { border: none; border-top: 1px solid #EAE5D9; margin: 12px 0; }
    </style>
  </head><body>
    <div class="brand">BAKEMILL</div>
    <div class="subtitle">Dispatch Note · HO Warehouse → Showroom Stock Transfer</div>
    <hr/>
    <div class="barcode-box">
      <svg id="transfer-barcode"></svg>
      <div class="caption">Transfer ID — scan or type this into GRN → Receive Stock</div>
    </div>
    <div class="grid">
      <div class="field"><div class="k">From</div><div class="v">${escapeHtml(d.from_branch_name)} (${escapeHtml(d.from_branch_code)})</div></div>
      <div class="field"><div class="k">To</div><div class="v">${escapeHtml(d.to_branch_name)} (${escapeHtml(d.to_branch_code)})</div></div>
      <div class="field"><div class="k">Driver</div><div class="v">${escapeHtml(d.driver_name)}${d.driver_phone ? " · " + escapeHtml(d.driver_phone) : ""}</div></div>
      <div class="field"><div class="k">Vehicle No.</div><div class="v">${escapeHtml(d.vehicle_no || "—")}</div></div>
      <div class="field"><div class="k">E-way No.</div><div class="v">${escapeHtml(d.eway_no || "—")}</div></div>
      <div class="field"><div class="k">Invoice / DN No.</div><div class="v">${escapeHtml(d.invoice_no || "—")}</div></div>
      <div class="field"><div class="k">Dispatched At</div><div class="v">${d.created_at ? new Date(d.created_at).toLocaleString("en-IN") : "—"}</div></div>
      <div class="field"><div class="k">Expected Delivery</div><div class="v">${d.expected_delivery_at ? new Date(d.expected_delivery_at).toLocaleDateString("en-IN") : "—"}</div></div>
    </div>
    <table>
      <thead><tr><th>Product</th><th>SKU</th><th style="text-align:right">Qty</th><th>Unit</th><th style="text-align:right">HO Supply Price</th><th style="text-align:right">Value</th></tr></thead>
      <tbody>${itemRows || `<tr><td colspan="6" style="color:#8B7355">No item detail recorded</td></tr>`}</tbody>
      ${totalValue > 0 ? `<tfoot><tr><td colspan="5" style="text-align:right;font-weight:700">Total HO Supply Value</td><td style="text-align:right;font-weight:700">${fmtRs(totalValue)}</td></tr></tfoot>` : ""}
    </table>
    <div style="font-size:10px;color:#8B7355;margin-top:4px">HO Supply Price = internal HO Transfer Rate at which this stock is billed to the showroom (not customer MRP).</div>
    ${d.notes ? `<div class="footer-note"><b>Notes:</b> ${escapeHtml(d.notes)}</div>` : ""}
    <div class="footer-note">Showroom manager: type or scan the Transfer ID above into <b>GRN → Inbound</b> to pull up this shipment, verify counts, and click Accept GRN.</div>
    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11/dist/JsBarcode.all.min.js"></script>
    <script>
      window.addEventListener('load', () => {
        try {
          JsBarcode('#transfer-barcode', ${JSON.stringify(d.number || "")}, {
            format: 'CODE128', width: 2, height: 60, fontSize: 14, margin: 4, displayValue: true,
          });
        } catch (e) {}
        setTimeout(() => window.print(), 400);
      });
    </script>
  </body></html>`);
  w.document.close();
}

// ---------- Create Dispatch Dialog ----------
function ProductQuickPicker({ onPick, testid = "dispatch-product-picker" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products?search=${encodeURIComponent(q)}`);
        setSuggestions((data || []).slice(0, 6));
      } catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Scan barcode or search product…"
        className="h-8 text-xs"
        data-testid={testid}
      />
      {suggestions.length > 0 && (
        <div className="absolute z-10 left-0 right-0 border border-[#EAE5D9] rounded mt-1 max-h-40 overflow-y-auto bg-white shadow-md" data-testid={`${testid}-results`}>
          {suggestions.map((p) => (
            <button
              key={p.id}
              onClick={() => { onPick(p); setSuggestions([]); setQ(""); }}
              data-testid={`${testid}-option-${p.id}`}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#FDFBF7] border-b border-[#EAE5D9] last:border-0"
            >
              <span className="font-medium">{p.name}</span> <span className="text-[#8B7355]">({p.sku})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateDispatchDialog({ onClose, onCreated, prefill = null }) {
  const [branches, setBranches] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [items, setItems] = useState(() => (prefill?.products || []).map((p) => (
    { product_id: p.id, name: p.name, sku: p.sku, unit: p.unit, quantity: p.prefill_qty ?? "", ho_transfer_cost: p.ho_transfer_cost }
  )));
  const [form, setForm] = useState({
    from_branch_id: "", to_branch_id: prefill?.to_branch_id || "",
    driver_name: "", driver_phone: "", vehicle_no: "", eway_no: "",
    invoice_no: "", expected_delivery_at: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/branches");
        setBranches(data || []);
        const wh = (data || []).find((b) => b.type === "warehouse");
        if (wh) setForm((f) => ({ ...f, from_branch_id: wh.id }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load branches");
      }
    })();
  }, []);

  useEffect(() => {
    if (!form.from_branch_id) { setStockMap({}); return; }
    (async () => {
      try {
        const { data } = await api.get(`/branch-stock?branch_id=${form.from_branch_id}`);
        const m = {};
        (data || []).forEach((s) => { m[s.product_id] = s.quantity; });
        setStockMap(m);
      } catch { setStockMap({}); }
    })();
  }, [form.from_branch_id]);

  const addItem = (p) => {
    if (items.some((it) => it.product_id === p.id)) { toast.error(`${p.name} already added`); return; }
    setItems((prev) => [...prev, { product_id: p.id, name: p.name, sku: p.sku, unit: p.unit, quantity: "", ho_transfer_cost: p.ho_transfer_cost }]);
  };
  const updateItemQty = (pid, qty) => setItems((prev) => prev.map((it) => it.product_id === pid ? { ...it, quantity: qty } : it));
  const removeItem = (pid) => setItems((prev) => prev.filter((it) => it.product_id !== pid));

  const submit = async () => {
    if (!form.from_branch_id || !form.to_branch_id) { toast.error("From & To branches required"); return; }
    if (form.from_branch_id === form.to_branch_id) { toast.error("From and To branches must differ"); return; }
    if (!form.driver_name.trim()) { toast.error("Driver name is required"); return; }
    if (!items.length) { toast.error("Add at least one product to dispatch"); return; }
    for (const it of items) {
      if (!it.quantity || Number(it.quantity) <= 0) { toast.error(`Enter a quantity for ${it.name}`); return; }
    }
    setBusy(true);
    try {
      const payload = { ...form, items: items.map((it) => ({ product_id: it.product_id, name: it.name, quantity: Number(it.quantity), unit: it.unit })) };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/dispatch", payload);
      toast.success(`Dispatched ${data.number} — stock deducted from HO warehouse, marked In Transit`);
      printDispatchNote(data);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create dispatch");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dispatch-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#C05621]" /> New Dispatch
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">
            Pick product, quantity & destination. On dispatch, stock is deducted
            from the source immediately and marked In Transit. A unique Transfer
            ID is generated — the showroom types it in to pull up the GRN.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">From branch (HO Warehouse)</Label>
              <Select value={form.from_branch_id} onValueChange={(v) => setForm({ ...form, from_branch_id: v })}>
                <SelectTrigger data-testid="dispatch-from-branch"><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">To branch (Showroom)</Label>
              <Select value={form.to_branch_id} onValueChange={(v) => setForm({ ...form, to_branch_id: v })}>
                <SelectTrigger data-testid="dispatch-to-branch"><SelectValue placeholder="Destination" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {branches.filter((b) => b.id !== form.from_branch_id).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Add product</Label>
            <ProductQuickPicker onPick={addItem} />
          </div>

          {items.length > 0 && (
            <div className="border border-[#EAE5D9] rounded-md divide-y divide-[#EAE5D9]" data-testid="dispatch-items-table">
              {items.map((it) => (
                <div key={it.product_id} className="flex items-center gap-2 p-2" data-testid={`dispatch-item-${it.product_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{it.name}</div>
                    <div className="text-[10px] text-[#8B7355]">
                      {it.sku} · Avail at source: {stockMap[it.product_id] ?? 0} {it.unit}
                      {" · "}
                      <span className={Number(it.ho_transfer_cost) > 0 ? "text-[#C05621]" : "text-amber-600"} data-testid={`dispatch-item-ho-rate-${it.product_id}`}>
                        HO rate: {Number(it.ho_transfer_cost) > 0 ? `₹${Number(it.ho_transfer_cost).toFixed(2)}` : "not set (batch rate will be used if available)"}
                      </span>
                    </div>
                  </div>
                  <Input
                    type="number" min="0" step="0.01"
                    value={it.quantity}
                    onChange={(e) => updateItemQty(it.product_id, e.target.value)}
                    className="w-24 h-8 text-xs"
                    placeholder="Qty"
                    data-testid={`dispatch-item-qty-${it.product_id}`}
                  />
                  <button onClick={() => removeItem(it.product_id)} data-testid={`dispatch-item-remove-${it.product_id}`} className="text-red-500 hover:text-red-700 text-xs px-1">✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">Driver name *</Label>
              <Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="dispatch-driver-name" placeholder="Ramesh Kumar" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">Driver phone</Label>
              <Input value={form.driver_phone} onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} data-testid="dispatch-driver-phone" placeholder="+91-98…" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">Vehicle no.</Label>
              <Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value.toUpperCase() })} data-testid="dispatch-vehicle-no" placeholder="MH-12-AB-1234" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">E-way no.</Label>
              <Input value={form.eway_no} onChange={(e) => setForm({ ...form, eway_no: e.target.value })} data-testid="dispatch-eway-no" placeholder="1234 5678 9012" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">Invoice / DN no.</Label>
              <Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="dispatch-invoice-no" placeholder="e.g. DN/2026/0087" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">Expected delivery</Label>
              <Input type="date" value={form.expected_delivery_at} onChange={(e) => setForm({ ...form, expected_delivery_at: e.target.value })} data-testid="dispatch-eta" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="dispatch-notes" placeholder="Handle with care, 30 boxes stacked, etc." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="dispatch-create-submit"
            className="bg-[#C05621] hover:bg-[#9C4221] text-white"
          >
            {busy ? "Dispatching…" : "Dispatch Now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Dispatch Detail Dialog ----------
function DispatchDetailDialog({ id, onClose, onChanged, user }) {
  const [dispatch, setDispatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signName, setSignName] = useState("");
  const [note, setNote] = useState("");
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [editingItems, setEditingItems] = useState(false);
  const isSuperAdmin = (user?.role_id || user?.role) === "super_admin";

  const openDelete = async () => {
    try {
      const { data } = await api.get(`/dispatch/${id}/delete-preview`);
      setDeletePreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load delete preview"); }
  };
  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/dispatch/${id}`);
      toast.success(`${data.number} deleted — ${data.restore_to_source_qty || 0} unit(s) restored to ${data.from_branch_name}`);
      setDeletePreview(null);
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete dispatch"); }
    finally { setDeleteBusy(false); }
  };
  const deleteEffects = deletePreview ? [
    deletePreview.reverse_from_showroom_qty > 0 && `Reverse ${deletePreview.reverse_from_showroom_qty} unit(s) already received at ${deletePreview.to_branch_name} (GRN ${(deletePreview.grn_numbers || []).join(", ")}) — blocked if that stock was already sold`,
    deletePreview.grn_numbers?.length > 0 && deletePreview.reverse_from_showroom_qty === 0 && `Remove the linked draft GRN ${(deletePreview.grn_numbers || []).join(", ")}`,
    deletePreview.status !== "cancelled" && deletePreview.restore_to_source_qty > 0 && `Restore ${deletePreview.restore_to_source_qty} unit(s) to ${deletePreview.from_branch_name} (HO Master Warehouse), including FEFO batches`,
    deletePreview.total_value > 0 && `Reverse the stock-transfer ledger entry of ₹${Number(deletePreview.total_value).toLocaleString("en-IN")}`,
  ] : [];

  const load = async () => {
    try {
      const { data } = await api.get(`/dispatch/${id}`);
      setDispatch(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dispatch");
      onClose();
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const pushStatus = async (nextStatus, requireSign = false) => {
    if (requireSign && !signName.trim()) { toast.error("Signature name required to mark signed"); return; }
    setBusy(true);
    try {
      const payload = { status: nextStatus };
      if (note.trim()) payload.note = note.trim();
      if (requireSign) payload.signature_name = signName.trim();
      await api.post(`/dispatch/${id}/status`, payload);
      toast.success(`Marked ${STATUS_META[nextStatus].label}`);
      setNote(""); setSignName("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally { setBusy(false); }
  };

  if (!dispatch) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="bg-white max-w-2xl">
          <div className="py-8 text-center text-[#8B7355]">Loading dispatch…</div>
        </DialogContent>
      </Dialog>
    );
  }

  const cur = dispatch.current_status;
  const canInTransit = cur === "dispatched";
  const canDelivered = cur === "in_transit";
  const canSign = cur === "delivered";
  const canCancel = cur === "dispatched" || cur === "in_transit";

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dispatch-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#C05621]" />
            {dispatch.number}
            <Badge className={`ml-2 border ${STATUS_META[cur]?.tone || ""}`}>
              {STATUS_META[cur]?.label || cur}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">
            {dispatch.from_branch_name} → {dispatch.to_branch_name}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-3">
          <InfoBlock icon={UserIcon} label="Driver" value={dispatch.driver_name} sub={dispatch.driver_phone} />
          <InfoBlock icon={Truck} label="Vehicle" value={dispatch.vehicle_no || "—"} />
          <InfoBlock icon={FileText} label="E-way / Invoice" value={dispatch.eway_no || "—"} sub={dispatch.invoice_no} />
          <InfoBlock icon={MapPin} label="Expected delivery" value={dispatch.expected_delivery_at ? fmtDate(dispatch.expected_delivery_at) : "Not set"} />
        </div>

        {(dispatch.items && dispatch.items.length > 0) && (
          <div className="border border-[#EAE5D9] rounded-md p-3 bg-[#FDFBF7]">
            <div className="text-[10px] uppercase text-[#8B7355] mb-2 flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5" /> Items dispatched
            </div>
            <ul className="text-sm space-y-1">
              {dispatch.items.map((it, i) => (
                <li key={i} className="text-[#2C1810] flex items-center justify-between gap-2">
                  <span>• {it.name || it.product_id} × <span className="font-medium">{it.quantity}</span> {it.unit}</span>
                  <span className="text-xs text-[#C05621]" data-testid={`dispatch-detail-ho-rate-${i}`}>
                    {Number(it.unit_cost) > 0 ? `@ ₹${Number(it.unit_cost).toFixed(2)} = ₹${Number(it.value ?? it.unit_cost * it.quantity).toFixed(2)}` : "no HO rate"}
                  </span>
                </li>
              ))}
            </ul>
            {dispatch.items.some((it) => Number(it.unit_cost) > 0) && (
              <div className="text-xs font-semibold text-right mt-2 pt-2 border-t border-[#EAE5D9]" data-testid="dispatch-detail-total-value">
                Total HO Supply Value: ₹{dispatch.items.reduce((s, it) => s + (Number(it.value) || (Number(it.unit_cost) || 0) * (Number(it.quantity) || 0)), 0).toFixed(2)}
              </div>
            )}
          </div>
        )}

        {dispatch.transfer && (dispatch.transfer.items || dispatch.transfer.product_name) && (
          <div className="border border-[#EAE5D9] rounded-md p-3 bg-[#FDFBF7]">
            <div className="text-[10px] uppercase text-[#8B7355] mb-2 flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5" /> Linked stock transfer
            </div>
            {dispatch.transfer.items ? (
              <ul className="text-sm space-y-1">
                {dispatch.transfer.items.map((it, i) => (
                  <li key={i} className="text-[#2C1810]">
                    • {it.name || it.product_id} × <span className="font-medium">{it.quantity}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-[#2C1810]">
                {dispatch.transfer.product_name} × <span className="font-medium">{dispatch.transfer.quantity}</span>
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        <div>
          <div className="text-[10px] uppercase text-[#8B7355] mb-2 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Status timeline
          </div>
          <ol className="relative border-l-2 border-[#EAE5D9] pl-4 space-y-3">
            {(dispatch.timeline || []).map((t, i) => {
              const meta = STATUS_META[t.status];
              const Icon = meta?.icon || Clock;
              return (
                <li key={i} className="relative">
                  <span className={`absolute -left-[22px] top-1 w-4 h-4 rounded-full border-2 border-white ${meta?.tone.split(" ")[0] || "bg-slate-200"} flex items-center justify-center`}>
                    <Icon className="w-2.5 h-2.5" />
                  </span>
                  <div className="text-sm text-[#2C1810] font-medium">{meta?.label || t.status}</div>
                  <div className="text-[11px] text-[#8B7355]">
                    {fmtDate(t.at)} · by {t.by_name || t.by}
                    {t.signature_name && <> · signed <span className="font-medium text-emerald-700">{t.signature_name}</span></>}
                  </div>
                  {t.note && <div className="text-[11px] text-[#5C4033] mt-0.5 italic">"{t.note}"</div>}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Actions */}
        {cur !== "signed" && cur !== "cancelled" && (
          <div className="border-t border-[#EAE5D9] pt-3 space-y-2">
            <Label className="text-[10px] uppercase text-[#8B7355]">Update status</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (visible on timeline)" data-testid="dispatch-note" />
            {canSign && (
              <Input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Signature name (required to sign)" data-testid="dispatch-sign-name" />
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {canInTransit && (
                <Button disabled={busy} onClick={() => pushStatus("in_transit")} data-testid="dispatch-mark-in-transit" className="bg-amber-500 hover:bg-amber-600 text-white">
                  <Clock className="w-4 h-4 mr-1" /> Mark In Transit
                </Button>
              )}
              {canDelivered && (
                <Button disabled={busy} onClick={() => pushStatus("delivered")} data-testid="dispatch-mark-delivered" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Delivered
                </Button>
              )}
              {canSign && (
                <Button disabled={busy} onClick={() => pushStatus("signed", true)} data-testid="dispatch-mark-signed" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Signature className="w-4 h-4 mr-1" /> Confirm Signed
                </Button>
              )}
              {canCancel && (
                <Button disabled={busy} onClick={() => pushStatus("cancelled")} data-testid="dispatch-mark-cancelled" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                  <XCircle className="w-4 h-4 mr-1" /> Cancel
                </Button>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          {isSuperAdmin ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={openDelete} data-testid="dispatch-delete-btn" className="border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-1" /> Delete dispatch
              </Button>
              {!["delivered", "signed", "cancelled"].includes(cur) && dispatch.items?.length > 0 && (
                <Button variant="outline" onClick={() => setEditingItems(true)} data-testid="dispatch-edit-items-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                  <Pencil className="w-4 h-4 mr-1" /> Edit items
                </Button>
              )}
            </div>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => printDispatchNote(dispatch)} data-testid="dispatch-detail-print-btn">
              <Printer className="w-4 h-4 mr-1" /> Print Dispatch Note
            </Button>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
      <ForceDeleteDialog open={!!deletePreview} onOpenChange={(o) => !o && setDeletePreview(null)} title={`Force-delete Dispatch${["delivered", "signed"].includes(cur) ? ` (${STATUS_META[cur]?.label})` : ""}`}
        subject={dispatch.number} effects={deleteEffects} onConfirm={confirmDelete} busy={deleteBusy} testid="dispatch-force-delete" />
      {editingItems && <DispatchItemsEditor dispatch={dispatch} onClose={() => setEditingItems(false)} onSaved={() => { load(); onChanged?.(); }} />}
    </Dialog>
  );
}

function InfoBlock({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-[#EAE5D9] rounded-md p-3">
      <div className="text-[10px] uppercase text-[#8B7355] flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-sm font-medium text-[#2C1810]">{value || "—"}</div>
      {sub && <div className="text-[11px] text-[#8B7355] mt-0.5">{sub}</div>}
    </div>
  );
}
